/* F+SH funnel client logic.
 * Part 1: pure rules (no DOM), unit-tested in tests/fsh-rules.test.js.
 * Part 2: DOM wiring with a single render().
 * Phase 2 scaffold: empty shells, filled in Phases 4 and 5. */
(function (root) {
  'use strict';

  /* ---------- Part 1: rules ----------
   * data  = the #fsh-data block (BUILD_SPEC 6.2). Prices are cents.
   * state = { funnel, decisions: { stepId: true|false }, picks: { 'stepId:productId': variantId }, pending }
   * Nothing here rounds. The cart rounds; we show exact values and let /cart.js win. */
  var MAX_CODES = 5; // Shopify: up to 5 product or order discount codes per order

  // Strips binary float noise (19.99 * 100 = 1998.9999999999998) without rounding to a cent.
  function clean(n) { return Number(n.toPrecision(12)); }

  function nextId(step, answeredYes) {
    return answeredYes ? step.yesNext : step.noNext;
  }

  function walkPath(data, decisions) {
    var path = [];
    var cur = data.start;
    while (cur) {
      if (path.indexOf(cur) !== -1) {
        console.error('[fsh] funnel loops back to step "' + cur + '"; sending the buyer to Summary.');
        return { path: path, atSummary: true, cycle: true };
      }
      var step = data.steps[cur];
      if (!step) {
        console.error('[fsh] step "' + cur + '" is routed to but missing from the funnel steps list; sending the buyer to Summary.');
        return { path: path, atSummary: true, cycle: false };
      }
      path.push(cur);
      if (decisions[cur] === undefined) return { path: path, atSummary: false, cycle: false };
      cur = nextId(step, decisions[cur]);
    }
    return { path: path, atSummary: true, cycle: false };
  }

  function findVariant(product, selection) {
    return product.variants.find(function (v) {
      return v.options.length === selection.length && v.options.every(function (o, i) { return o === selection[i]; });
    }) || null;
  }

  // Is `value` for option `index` buyable, given the other options already picked? (null = not picked yet)
  function valueAvailable(product, selection, index, value) {
    return product.variants.some(function (v) {
      if (!v.available || v.options[index] !== value) return false;
      return selection.every(function (sel, i) { return i === index || sel === null || sel === undefined || v.options[i] === sel; });
    });
  }

  function soldOut(step) {
    return step.fixed.some(function (f) { return !f.available; });
  }

  // Lines for every fixed variant plus each choice product the buyer has picked a variant for.
  function resolveVariants(step, picks, stepId) {
    var lines = step.fixed.map(function (f) { return { variantId: f.variantId, price: f.price }; });
    var missing = [];
    step.choice.forEach(function (p) {
      var pickedId = picks[stepId + ':' + p.productId];
      var v = p.variants.find(function (x) { return x.id === pickedId; });
      if (v) lines.push({ variantId: v.id, price: v.price });
      else missing.push(p.productId);
    });
    return {
      lines: lines,
      ids: lines.map(function (l) { return l.variantId; }),
      missing: missing,
    };
  }

  // For display only: fills each unpicked choice product with its cheapest buyable variant so a
  // step shows a price before the buyer has picked sizes. Never used for what gets added to the cart.
  function provisionalPicks(step, picks, stepId) {
    var out = Object.assign({}, picks);
    step.choice.forEach(function (p) {
      var key = stepId + ':' + p.productId;
      if (out[key]) return;
      var pool = p.variants.filter(function (v) { return v.available; });
      if (!pool.length) pool = p.variants;
      var cheapest = pool.reduce(function (a, b) { return !a || b.price < a.price ? b : a; }, null);
      if (cheapest) out[key] = cheapest.id;
    });
    return out;
  }

  function stepPrice(step, picks, stepId) {
    var retail = resolveVariants(step, picks, stepId).lines.reduce(function (sum, l) { return sum + l.price; }, 0);
    var price = retail;
    if (step.code && step.off) {
      var value = Number(step.off.value);
      if (step.off.type === 'percent') price = clean(retail * (100 - value) / 100);
      else price = Math.max(0, retail - clean(value * 100));
    }
    return { retail: retail, price: price };
  }

  // Our exact price vs what the cart charged. The cart rounds each line to a cent, so allow half a cent per line.
  function stepMismatch(expected, actual, lineCount) {
    return Math.abs(expected - actual) > lineCount * 1 + 1e-6;
  }

  function acceptedCodes(data, path, decisions) {
    var codes = [];
    path.forEach(function (id) {
      var step = data.steps[id];
      if (decisions[id] === true && step && step.code && codes.indexOf(step.code) === -1) codes.push(step.code);
    });
    return codes;
  }

  function exceedsCodeLimit(codes) { return codes.length > MAX_CODES; }

  function funnelLines(cart, funnelHandle) {
    return (cart.items || []).filter(function (it) {
      var p = it.properties;
      return p && p._fsh_funnel === funnelHandle && p._fsh_step;
    });
  }

  // Summary numbers come from the cart, grouped by the _fsh_step line property.
  function summaryFromCart(cart, funnelHandle, order) {
    var byStep = {};
    var seen = [];
    funnelLines(cart, funnelHandle).forEach(function (it) {
      var id = it.properties._fsh_step;
      if (!byStep[id]) { byStep[id] = { step: id, retail: 0, price: 0, items: [], lineCount: 0 }; seen.push(id); }
      var row = byStep[id];
      row.retail += it.original_line_price;
      row.price += it.line_price;
      row.lineCount += 1;
      row.items.push({ title: it.product_title || it.title, variantTitle: it.variant_title || null, quantity: it.quantity });
    });
    var ids = (order || []).filter(function (id) { return byStep[id]; });
    seen.forEach(function (id) { if (ids.indexOf(id) === -1) ids.push(id); });
    var rows = ids.map(function (id) { return byStep[id]; });
    var subtotal = rows.reduce(function (s, r) { return s + r.price; }, 0);
    var retailTotal = rows.reduce(function (s, r) { return s + r.retail; }, 0);
    var count = funnelLines(cart, funnelHandle).reduce(function (s, it) { return s + it.quantity; }, 0);
    return { rows: rows, subtotal: subtotal, retailTotal: retailTotal, saved: retailTotal - subtotal, count: count };
  }

  // Saved state is only trusted while the cart still holds lines for every accepted step.
  function restoreState(saved, cart, data) {
    if (!saved || typeof saved !== 'object' || saved.funnel !== data.funnel || !saved.decisions) return null;
    var ids = Object.keys(saved.decisions);
    var known = ids.every(function (id) { return data.steps[id] && typeof saved.decisions[id] === 'boolean'; });
    if (!known) return null;
    var inCart = {};
    funnelLines(cart, data.funnel).forEach(function (it) { inCart[it.properties._fsh_step] = true; });
    var held = ids.every(function (id) { return saved.decisions[id] !== true || inCart[id]; });
    if (!held) return null;
    return { funnel: saved.funnel, decisions: Object.assign({}, saved.decisions), picks: Object.assign({}, saved.picks), pending: false };
  }

  var FshRules = {
    MAX_CODES: MAX_CODES,
    walkPath: walkPath,
    findVariant: findVariant,
    valueAvailable: valueAvailable,
    soldOut: soldOut,
    resolveVariants: resolveVariants,
    provisionalPicks: provisionalPicks,
    stepPrice: stepPrice,
    stepMismatch: stepMismatch,
    acceptedCodes: acceptedCodes,
    exceedsCodeLimit: exceedsCodeLimit,
    summaryFromCart: summaryFromCart,
    restoreState: restoreState,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = FshRules;
  root.FshRules = FshRules;

  /* ---------- Part 2: DOM ----------
   * One render() paints everything from state + the last /cart.js. Events are delegated on data-fsh-* attributes. */
  if (typeof document === 'undefined') return;

  var designMode = !!(root.Shopify && root.Shopify.designMode);
  var data = null;
  var i18n = {};
  var state = null;
  var cart = { items: [], item_count: 0 };
  var selections = {}; // 'stepId:productId' -> option values picked so far (null = not picked)
  var errors = {};     // stepId -> message
  var codeWarning = false;
  var bound = false;
  var leaving = null;        // step id still on screen while the page scrolls away from it
  var transitioning = false; // blocks clicks during that scroll

  function readJson(id) {
    var el = document.getElementById(id);
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }
  function fmt(cents) { return '$' + (cents / 100).toFixed(2); } // display only; USD store
  function t(key, vars) {
    var out = i18n[key] || '';
    Object.keys(vars || {}).forEach(function (k) { out = out.split('%' + k + '%').join(vars[k]); });
    return out;
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function stepEl(id) { return $('[data-fsh-step="' + id + '"]'); }
  function routeUrl(p) { return ((root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/') + p; }
  function unique(a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }); }

  /* ----- storage ----- */
  function storageKey() { return 'fsh:' + data.funnel; }
  function save() {
    try { root.sessionStorage.setItem(storageKey(), JSON.stringify({ funnel: state.funnel, decisions: state.decisions, picks: state.picks, pending: false })); } catch (e) { /* works without it */ }
  }
  function load() {
    try { return JSON.parse(root.sessionStorage.getItem(storageKey())); } catch (e) { return null; }
  }
  function forget() { try { root.sessionStorage.removeItem(storageKey()); } catch (e) { /* ignore */ } }

  /* ----- cart API ----- */
  function request(method, path, body) {
    var opts = { method: method, headers: { 'Accept': 'application/json' } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    return root.fetch(routeUrl(path), opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error(json.description || json.message || ('HTTP ' + res.status));
          err.status = res.status;
          throw err;
        }
        return json;
      });
    });
  }
  function fetchCart() { return request('GET', 'cart.js').then(function (c) { cart = c; return c; }); }

  /* ----- choice product selections ----- */
  function choiceProduct(stepId, productId) {
    return data.steps[stepId].choice.filter(function (p) { return p.productId === productId; })[0];
  }
  function selectionFor(stepId, p) {
    var key = stepId + ':' + p.productId;
    var picked = state.picks[key] && p.variants.filter(function (v) { return v.id === state.picks[key]; })[0];
    if (picked) return picked.options.slice();
    if (!selections[key]) {
      selections[key] = p.options.map(function (_, i) {
        var vals = unique(p.variants.map(function (v) { return v.options[i]; }));
        return vals.length === 1 ? vals[0] : null; // single-value options need no pick
      });
    }
    return selections[key];
  }
  function syncPick(stepId, p) {
    var key = stepId + ':' + p.productId;
    var sel = selectionFor(stepId, p);
    var v = sel.every(function (x) { return x !== null; }) ? findVariant(p, sel) : null;
    if (v && v.available) state.picks[key] = v.id; else delete state.picks[key];
  }
  function syncAllPicks() {
    Object.keys(data.steps).forEach(function (id) {
      data.steps[id].choice.forEach(function (p) { if (state.decisions[id] === undefined) syncPick(id, p); });
    });
  }

  /* ----- flow ----- */
  function autoSkip() {
    for (var guard = 0; guard < 50; guard++) {
      var walk = walkPath(data, state.decisions);
      if (walk.atSummary) return;
      var id = walk.path[walk.path.length - 1];
      var step = data.steps[id];
      if (state.decisions[id] === undefined && step.hasNo && soldOut(step)) state.decisions[id] = false;
      else return;
    }
  }

  function setBusy(busy) {
    state.pending = busy;
    $$('[data-fsh-step]').forEach(function (el) { if (busy) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy'); });
    render();
  }

  // Called after render(), so the next panel is already shown. Panels scroll inside themselves, so reset to the top.
  function revealAndFocus() {
    var walk = walkPath(data, state.decisions);
    var target = walk.atSummary ? $('[data-fsh-section="fsh-summary"]') : stepEl(walk.path[walk.path.length - 1]);
    if (!target) return;
    target.scrollTop = 0;
    var heading = $('[data-fsh-title], [data-fsh-summary-heading]', target);
    if (heading) heading.focus({ preventScroll: true });
    if (designMode) target.scrollIntoView({ block: 'start' });
  }

  // The buyer cannot scroll (the page is overflow:hidden), so after an answer the page scrolls itself from the
  // step just answered to the next panel, then drops the old step and resets to the top.
  function advance(fromId) {
    var walk = walkPath(data, state.decisions);
    var target = walk.atSummary ? $('[data-fsh-section="fsh-summary"]') : stepEl(walk.path[walk.path.length - 1]);
    var from = stepEl(fromId);
    var reduce = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (designMode || reduce || !from || !target || from === target) { render(); revealAndFocus(); return; }
    transitioning = true;
    leaving = fromId;
    render();
    from.style.order = '0';
    if (target.parentNode === from.parentNode) target.style.order = '1';
    target.scrollTop = 0;
    var header = $('.fsh-header');
    var top = target.getBoundingClientRect().top + root.scrollY - (header ? header.offsetHeight : 0);
    root.scrollTo({ top: top, behavior: 'smooth' });
    var waited = 0;
    var stalled = 0;
    var lastY = root.scrollY;
    var timer = root.setInterval(function () {
      waited += 40;
      if (root.scrollY !== lastY) { lastY = root.scrollY; stalled = 0; } else stalled += 40;
      // Done when it arrives, or if the browser is not animating (hidden tab) or something is stuck.
      if (Math.abs(root.scrollY - top) >= 1 && stalled < 400 && waited <= 1500) return;
      root.clearInterval(timer);
      leaving = null;
      transitioning = false;
      from.style.order = '';
      target.style.order = '';
      render();
      root.scrollTo({ top: 0, behavior: 'instant' });
      revealAndFocus();
    }, 40);
  }

  function reconcile(path) {
    var sum = summaryFromCart(cart, data.funnel, path);
    var warn = false;
    sum.rows.forEach(function (row) {
      var step = data.steps[row.step];
      if (!step || state.decisions[row.step] !== true) return;
      var expected = stepPrice(step, state.picks, row.step).price;
      var bad = stepMismatch(expected, row.price, row.lineCount);
      var el = stepEl(row.step);
      if (el) { if (bad) el.setAttribute('data-fsh-mismatch', ''); else el.removeAttribute('data-fsh-mismatch'); }
      if (bad) {
        console.warn('[fsh] step "' + row.step + '" expected ' + expected + ' cents but the cart charges ' + row.price + '. Check its discount code and the step\'s discount fields.');
        if (step.code) warn = true;
      }
    });
    codeWarning = warn;
  }

  function answerNo(id) {
    state.decisions[id] = false;
    autoSkip();
    save();
    advance(id);
  }

  function answerYes(id) {
    var step = data.steps[id];
    var resolved = resolveVariants(step, state.picks, id);
    if (resolved.missing.length || state.pending || soldOut(step)) return;
    errors[id] = '';
    setBusy(true);

    var firstYes = !Object.keys(state.decisions).some(function (k) { return state.decisions[k] === true; });
    var decisions = Object.assign({}, state.decisions);
    decisions[id] = true;
    var path = walkPath(data, decisions).path;
    var codes = acceptedCodes(data, path, decisions);
    if (exceedsCodeLimit(codes)) console.warn('[fsh] this path needs ' + codes.length + ' discount codes; Shopify allows ' + 5 + ' per order.');

    var items = resolved.ids.map(function (vid) {
      return { id: vid, quantity: 1, properties: { _fsh_funnel: data.funnel, _fsh_step: id } };
    });

    var chain = firstYes ? request('POST', 'cart/clear.js') : Promise.resolve();
    chain.then(function () { return request('POST', 'cart/add.js', { items: items }); })
      .then(function () { return request('POST', 'cart/update.js', { discount: codes.join(',') }); })
      .then(fetchCart)
      .then(function () {
        state.decisions[id] = true;
        reconcile(path);
        autoSkip();
        state.pending = false;
        $$('[data-fsh-step]').forEach(function (el) { el.removeAttribute('aria-busy'); });
        save();
        advance(id);
      })
      .catch(function (err) {
        errors[id] = err.status === 422 ? (err.message || t('unavailable')) : t('cart_error');
        console.error('[fsh] could not add step "' + id + '":', err);
        setBusyOff();
      });
  }
  function setBusyOff() {
    state.pending = false;
    $$('[data-fsh-step]').forEach(function (el) { el.removeAttribute('aria-busy'); });
    render();
  }

  function startOver() {
    if (state.pending) return;
    state.pending = true;
    render();
    request('POST', 'cart/clear.js')
      .then(function () { return request('POST', 'cart/update.js', { discount: '' }); })
      .then(fetchCart)
      .then(function () {
        state = { funnel: data.funnel, decisions: {}, picks: {}, pending: false };
        selections = {}; errors = {}; codeWarning = false;
        forget();
        syncAllPicks();
        autoSkip();
        $$('[data-fsh-mismatch]').forEach(function (el) { el.removeAttribute('data-fsh-mismatch'); });
        render();
        root.scrollTo({ top: 0, behavior: 'instant' });
        var first = stepEl(data.start);
        if (first) {
          first.scrollTop = 0;
          var h = $('[data-fsh-title]', first);
          if (h) h.focus({ preventScroll: true });
        }
      })
      .catch(function (err) {
        console.error('[fsh] start over failed:', err);
        state.pending = false;
        errors[data.start] = t('cart_error');
        render();
      });
  }

  /* ----- render ----- */
  function renderTile(tile, stepId, step, answered) {
    var p = choiceProduct(stepId, Number(tile.getAttribute('data-product-id')));
    if (!p) return;
    var sel = selectionFor(stepId, p);
    var picked = sel.every(function (x) { return x !== null; }) ? findVariant(p, sel) : null;
    var multi = p.options.map(function (_, i) { return unique(p.variants.map(function (v) { return v.options[i]; })).length > 1; });
    var shown = sel.filter(function (x, i) { return x !== null && multi[i]; });
    var name = $('[data-fsh-tile-name]', tile);
    if (name) name.textContent = shown.length ? p.name + ' · ' + shown.join(' / ') : p.name;
    var price = $('[data-fsh-tile-price]', tile);
    if (price) {
      price.textContent = picked ? fmt(picked.price)
        : t('from', { price: fmt(Math.min.apply(null, p.variants.map(function (v) { return v.price; }))) });
    }
    $$('[data-fsh-pill]', tile).forEach(function (btn) {
      var i = Number(btn.getAttribute('data-option-index'));
      var value = btn.getAttribute('data-value');
      var ok = valueAvailable(p, sel, i, value);
      btn.setAttribute('aria-pressed', sel[i] === value ? 'true' : 'false');
      btn.disabled = answered || !ok;
      btn.setAttribute('aria-label', ok ? value : value + ' (' + t('sold_out') + ')');
    });
    var hint = $('[data-fsh-pick-hint]', tile);
    if (hint) { hint.hidden = answered || !!picked; hint.textContent = t('pick_prompt'); }
  }

  function render() {
    if (!data) return;
    var walk = walkPath(data, state.decisions);
    var path = walk.path;

    $$('[data-fsh-step]').forEach(function (el) {
      var id = el.getAttribute('data-fsh-step');
      var step = data.steps[id];
      if (!step) return;
      var pos = path.indexOf(id);
      // One step on screen at a time: the one awaiting an answer. Answered steps and Summary replace it.
      if (!designMode) el.hidden = !((!walk.atSummary && pos === path.length - 1) || leaving === id);
      if (pos === -1) return;
      var answered = state.decisions[id] !== undefined;
      var num = $('[data-fsh-step-num]', el);
      if (num) num.textContent = t('step_label', { n: pos + 1 });

      $$('[data-fsh-tile][data-product-id]', el).forEach(function (tile) {
        if (!tile.hasAttribute('data-variant-id')) renderTile(tile, id, step, answered);
      });

      var missing = resolveVariants(step, state.picks, id).missing.length;
      var price = stepPrice(step, provisionalPicks(step, state.picks, id), id);
      var saved = price.retail - price.price;
      var shownPrice = price.price === 0 ? t('free') : fmt(price.price);
      var priceEl = $('[data-fsh-price]', el);
      if (priceEl) priceEl.textContent = (missing && step.choice.length && price.price !== 0) ? t('from', { price: fmt(price.price) }) : shownPrice;
      var retailEl = $('[data-fsh-retail]', el);
      if (retailEl) retailEl.textContent = saved > 0 ? fmt(price.retail) : '';
      var saveEl = $('[data-fsh-save]', el);
      if (saveEl) saveEl.textContent = saved > 0 ? t('save', { amount: fmt(saved) }) : '';

      var actions = $('[data-fsh-actions]', el);
      if (actions) actions.hidden = answered;
      var blocked = missing > 0 || soldOut(step) || state.pending;
      var yes = $('[data-fsh-yes]', el);
      if (yes) { yes.setAttribute('aria-disabled', blocked ? 'true' : 'false'); }
      var no = $('[data-fsh-no]', el);
      if (no) no.disabled = state.pending;

      var status = $('[data-fsh-status]', el);
      if (status) {
        status.hidden = !answered;
        var text = $('[data-fsh-status-text]', status);
        if (text) text.textContent = state.decisions[id] ? t('added') : t('skipped');
      }
      var err = $('[data-fsh-error]', el);
      var message = errors[id] || (!answered && soldOut(step) && !step.hasNo ? t('sold_out') : '');
      if (err) { err.textContent = message; err.hidden = !message; }
    });

    var label = $('[data-fsh-step-label]');
    if (label) label.textContent = walk.atSummary ? t('summary_label') : t('step_label', { n: path.length });
    var count = $('[data-fsh-cart-count]');
    if (count) count.textContent = t('cart_count', { n: cart.item_count || 0 });

    renderSummary(walk);
  }

  function renderSummary(walk) {
    var section = $('[data-fsh-section="fsh-summary"]');
    if (!section) return;
    if (!designMode) section.hidden = !walk.atSummary;
    if (!walk.atSummary && !designMode) return;

    var heading = $('[data-fsh-summary-heading]', section);
    if (heading && data.summaryHeading) heading.textContent = data.summaryHeading;
    var checkout = $('[data-fsh-checkout]', section);
    if (checkout && data.checkoutLabel) checkout.textContent = data.checkoutLabel;
    if (checkout) checkout.disabled = state.pending;

    var sum = summaryFromCart(cart, data.funnel, walk.path);
    var rows = $('[data-fsh-summary-rows]', section);
    if (rows) {
      rows.textContent = '';
      sum.rows.forEach(function (row) {
        var wrap = document.createElement('div');
        wrap.className = 'fsh-summary__line';
        var head = document.createElement('div');
        head.className = 'fsh-summary__line-head';
        var name = document.createElement('div');
        name.className = 'fsh-subhead';
        name.textContent = data.steps[row.step] ? data.steps[row.step].title : row.step;
        var prices = document.createElement('div');
        prices.className = 'fsh-summary__prices';
        if (row.retail !== row.price) {
          var was = document.createElement('div');
          was.className = 'fsh-summary__was';
          was.textContent = fmt(row.retail);
          prices.appendChild(was);
        }
        var now = document.createElement('div');
        now.className = 'fsh-summary__now';
        now.textContent = row.price === 0 ? t('free') : fmt(row.price);
        prices.appendChild(now);
        head.appendChild(name); head.appendChild(prices);
        var list = document.createElement('ul');
        list.className = 'fsh-summary__items';
        row.items.forEach(function (it) {
          var li = document.createElement('li');
          li.textContent = it.title + (it.variantTitle ? ' · ' + it.variantTitle : '') + (it.quantity > 1 ? ' × ' + it.quantity : '');
          list.appendChild(li);
        });
        wrap.appendChild(head); wrap.appendChild(list);
        rows.appendChild(wrap);
      });
    }
    var subtotal = $('[data-fsh-subtotal]', section);
    if (subtotal) subtotal.textContent = fmt(sum.subtotal);
    var savings = $('[data-fsh-savings]', section);
    if (savings) { savings.textContent = sum.saved > 0 ? t('you_save', { amount: fmt(sum.saved) }) : ''; savings.hidden = sum.saved <= 0; }
    var warn = $('[data-fsh-code-warning]', section);
    if (warn) warn.hidden = !codeWarning;
  }

  /* ----- events ----- */
  function onClick(e) {
    var el = e.target.closest && e.target.closest('[data-fsh-yes], [data-fsh-no], [data-fsh-pill], [data-fsh-checkout], [data-fsh-start-over]');
    if (!el || !data || transitioning) return;
    var stepNode = el.closest('[data-fsh-step]');
    var id = stepNode && stepNode.getAttribute('data-fsh-step');
    if (el.hasAttribute('data-fsh-yes')) {
      if (el.getAttribute('aria-disabled') === 'true') return;
      answerYes(id);
    } else if (el.hasAttribute('data-fsh-no')) {
      if (!state.pending && state.decisions[id] === undefined) answerNo(id);
    } else if (el.hasAttribute('data-fsh-pill')) {
      if (state.decisions[id] !== undefined || state.pending) return;
      var p = choiceProduct(id, Number(el.getAttribute('data-product-id')));
      if (!p) return;
      var sel = selectionFor(id, p);
      sel[Number(el.getAttribute('data-option-index'))] = el.getAttribute('data-value');
      selections[id + ':' + p.productId] = sel;
      syncPick(id, p);
      render();
    } else if (el.hasAttribute('data-fsh-checkout')) {
      if (!state.pending) root.location.href = routeUrl('checkout');
    } else if (el.hasAttribute('data-fsh-start-over')) {
      startOver();
    }
  }

  // Steps are sized to the screen minus the pinned header, so publish the header's real height to CSS.
  function syncHeaderHeight() {
    var header = $('.fsh-header');
    if (header) document.documentElement.style.setProperty('--fsh-header-h', header.offsetHeight + 'px');
  }

  function init() {
    syncHeaderHeight();
    if ('scrollRestoration' in root.history) root.history.scrollRestoration = 'manual'; // the page only ever rests at the top
    data = readJson('fsh-data');
    i18n = readJson('fsh-i18n') || {};
    if (!data) return;
    if (!data.start) data.start = Object.keys(data.steps)[0]; // JSON keeps the funnel's step order
    if (!bound) { document.addEventListener('click', onClick); root.addEventListener('resize', syncHeaderHeight); bound = true; }
    state = { funnel: data.funnel, decisions: {}, picks: {}, pending: false };
    selections = {}; errors = {};
    fetchCart().catch(function () { cart = { items: [], item_count: 0 }; }).then(function () {
      var restored = restoreState(load(), cart, data);
      if (restored) {
        state = restored;
        reconcile(walkPath(data, state.decisions).path);
      }
      syncAllPicks();
      autoSkip();
      render();
    });
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})(typeof window !== 'undefined' ? window : globalThis);
